import React from 'react';
import { AlertTriangle, CheckCircle2, Clock, FileText, Loader2, X } from 'lucide-react';
import { FEATURE_LITIGATION } from '../../../constants';
import type { PortalDocumentOpenFields } from '../portalDocuments';
import { JudicatureDemoJobCard } from './JudicatureDemoJobCard';

export interface LitigationSummary {
  claims_gefunden?: number;
  davon_kritisch?: number;
  davon_stark?: number;
  davon_mittel?: number;
}

export interface LitigationUploadResult {
  type: 'success' | 'error';
  message?: string;
  pdfUrl?: string;
  summary?: LitigationSummary;
  pdfLinkExpiresAt?: string;
  pdfGenerationNote?: string;
}

/** Nur diese Komponente aktualisiert jede Sekunde – verhindert Flackern im restlichen Portal */
export function LitigationPdfCountdown({ expiresAtIso }: { expiresAtIso?: string }) {
  const endMs = React.useMemo(() => {
    if (expiresAtIso) {
      const t = new Date(expiresAtIso).getTime();
      if (!Number.isNaN(t)) return t;
    }
    return Date.now() + 30 * 60 * 1000;
  }, [expiresAtIso]);

  const [remainingSec, setRemainingSec] = React.useState(() =>
    Math.max(0, Math.floor((endMs - Date.now()) / 1000)),
  );

  React.useEffect(() => {
    setRemainingSec(Math.max(0, Math.floor((endMs - Date.now()) / 1000)));
  }, [endMs]);

  React.useEffect(() => {
    const id = window.setInterval(() => {
      setRemainingSec(Math.max(0, Math.floor((endMs - Date.now()) / 1000)));
    }, 1000);
    return () => window.clearInterval(id);
  }, [endMs]);

  if (remainingSec <= 0) return null;

  const mm = Math.floor(remainingSec / 60);
  const ss = remainingSec % 60;

  return (
    <div
      className="flex items-center gap-2 rounded-xl bg-slate-800/80 border border-slate-600/50 px-3 py-2 text-sm text-slate-200 [contain:layout]"
      style={{ minHeight: '2.75rem' }}
    >
      <Clock className="w-4 h-4 text-amber-400 flex-shrink-0 pointer-events-none" aria-hidden />
      <p className="leading-snug m-0 flex-1 min-w-0">
        Empfohlen: PDF innerhalb von <strong className="text-white">30 Min.</strong> öffnen — noch ca.{' '}
        <span className="inline-block min-w-[5.25ch] tabular-nums text-amber-300 font-semibold text-right">
          {String(mm).padStart(2, '0')}:{String(ss).padStart(2, '0')}
        </span>
      </p>
    </div>
  );
}

export interface LitigationViewProps {
  textMinChars: number;
  uploadMode: 'pdf' | 'text';
  onUploadModeChange: (mode: 'pdf' | 'text') => void;
  files: File[];
  onFilesChange: (files: File[]) => void;
  schriftsatzText: string;
  onSchriftsatzTextChange: (text: string) => void;
  optIn: boolean;
  onOptInChange: (checked: boolean) => void;
  validationError: string;
  onValidationErrorClear: () => void;
  uploading: boolean;
  uploadResult: LitigationUploadResult | null;
  onUpload: () => void;
  customerId: string;
  customerStatus?: string;
  customerEmail?: string;
  onDocumentReady: (fields: PortalDocumentOpenFields) => void | Promise<void>;
}

export const LitigationView: React.FC<LitigationViewProps> = ({
  textMinChars,
  uploadMode,
  onUploadModeChange,
  files,
  onFilesChange,
  schriftsatzText,
  onSchriftsatzTextChange,
  optIn,
  onOptInChange,
  validationError,
  onValidationErrorClear,
  uploading,
  uploadResult,
  onUpload,
  customerId,
  customerStatus,
  customerEmail,
  onDocumentReady,
}) => (
  <div className="space-y-6 max-w-2xl mx-auto">
    <div>
      <h1 className="text-2xl font-bold text-white">{FEATURE_LITIGATION}</h1>
      <p className="text-amber-400/80 text-sm font-medium mt-1">
        Teil von Orbis Premium · Closed Beta – Bewerbung auf Anfrage.
      </p>
      <p className="text-slate-400 mt-1">Gegnerischen Schriftsatz hochladen · KI analysiert · Strategisches PDF</p>
    </div>

    <JudicatureDemoJobCard
      customerId={customerId}
      customerStatus={customerStatus}
      customerEmail={customerEmail}
      onDocumentReady={onDocumentReady}
    />

    {uploadResult?.type === 'success' && (
      <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-5 space-y-3">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-emerald-400 font-bold">Analyse abgeschlossen!</p>
            {uploadResult.summary && (
              <div className="flex flex-wrap gap-3 mt-2">
                {uploadResult.summary.claims_gefunden !== undefined && (
                  <span className="text-xs bg-slate-800 text-slate-300 px-2 py-1 rounded-lg">
                    {uploadResult.summary.claims_gefunden} Behauptungen
                  </span>
                )}
                {(uploadResult.summary.davon_kritisch ?? 0) > 0 && (
                  <span className="text-xs bg-red-500/20 text-red-400 border border-red-500/30 px-2 py-1 rounded-lg font-bold">
                    {uploadResult.summary.davon_kritisch} Kritisch
                  </span>
                )}
                {(uploadResult.summary.davon_stark ?? 0) > 0 && (
                  <span className="text-xs bg-orange-500/20 text-orange-400 border border-orange-500/30 px-2 py-1 rounded-lg font-bold">
                    {uploadResult.summary.davon_stark} Stark
                  </span>
                )}
                {(uploadResult.summary.davon_mittel ?? 0) > 0 && (
                  <span className="text-xs bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 px-2 py-1 rounded-lg">
                    {uploadResult.summary.davon_mittel} Mittel
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        {uploadResult.pdfUrl ? (
          <>
            <a
              href={uploadResult.pdfUrl}
              target="_blank"
              rel="noreferrer"
              download
              className="flex items-center justify-center gap-2 w-full bg-amber-500 hover:bg-amber-400 text-black font-bold py-2.5 rounded-xl transition-colors text-sm"
            >
              <FileText className="w-4 h-4" /> Strategie-PDF herunterladen
            </a>
            <LitigationPdfCountdown expiresAtIso={uploadResult.pdfLinkExpiresAt} />
          </>
        ) : (
          <div className="space-y-3">
            <p className="text-slate-400 text-xs">
              Das Strategie-PDF laden Sie hier herunter, sobald der Server den Link erzeugt hat (Nextcloud/Gotenberg).
              Bis dahin nutzen Sie die Kennzahlen oben; technische Hinweise siehe unten.
            </p>
            <LitigationPdfCountdown expiresAtIso={uploadResult.pdfLinkExpiresAt} />
            {uploadResult.pdfGenerationNote && (
              <p className="text-amber-200/90 text-xs font-mono bg-amber-950/40 border border-amber-500/20 rounded-lg p-2 break-words">
                {uploadResult.pdfGenerationNote}
              </p>
            )}
          </div>
        )}
      </div>
    )}

    {uploadResult?.type === 'error' && (
      <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-5 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-red-400 font-bold">Analyse fehlgeschlagen</p>
          <p className="text-slate-300 text-sm mt-1">
            {uploadResult.message || 'Bitte versuchen Sie es erneut oder kontaktieren Sie office@arkorbit.at'}
          </p>
        </div>
      </div>
    )}

    <div className={`bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-5${uploading ? ' opacity-90 pointer-events-none' : ''}`}>
      <div className="flex rounded-xl bg-slate-800/60 p-1 gap-1">
        <button
          type="button"
          onClick={() => {
            onUploadModeChange('pdf');
            onValidationErrorClear();
          }}
          className={`flex-1 text-xs font-bold py-2 rounded-lg transition-colors ${
            uploadMode === 'pdf' ? 'bg-amber-500 text-black' : 'text-slate-400 hover:text-white'
          }`}
        >
          PDF hochladen
        </button>
        <button
          type="button"
          onClick={() => {
            onUploadModeChange('text');
            onValidationErrorClear();
          }}
          className={`flex-1 text-xs font-bold py-2 rounded-lg transition-colors ${
            uploadMode === 'text' ? 'bg-amber-500 text-black' : 'text-slate-400 hover:text-white'
          }`}
        >
          Text eingeben
        </button>
      </div>

      {uploadMode === 'pdf' ? (
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">
              Dokumente (PDF) · bis zu 10
            </label>
            {files.length > 0 && (
              <span className="text-xs text-blue-400 font-medium">
                {files.length} Datei{files.length > 1 ? 'en' : ''} ausgewählt
              </span>
            )}
          </div>

          {files.length > 0 ? (
            <div className="space-y-2">
              {files.map((f, i) => (
                <div
                  key={`${f.name}-${f.size}-${i}`}
                  className="flex items-center justify-between bg-slate-800/60 border border-slate-700 rounded-lg px-3 py-2"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="w-4 h-4 text-blue-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-white text-xs font-medium truncate max-w-[220px]">{f.name}</p>
                      <p className="text-slate-500 text-[10px]">{(f.size / 1024 / 1024).toFixed(1)} MB</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => onFilesChange(files.filter((_, j) => j !== i))}
                    className="text-red-400 hover:text-red-300 ml-2 flex-shrink-0"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}

              {files.length < 10 && (
                <label className="flex items-center justify-center gap-2 border border-dashed border-slate-600 hover:border-slate-500 rounded-xl py-2.5 cursor-pointer text-slate-400 text-xs transition-all">
                  <input
                    type="file"
                    accept=".pdf"
                    multiple
                    className="hidden"
                    onChange={(e) =>
                      onFilesChange([...files, ...Array.from(e.target.files || [])].slice(0, 10))
                    }
                  />
                  <FileText className="w-3.5 h-3.5" />
                  Weiteres Dokument hinzufügen ({10 - files.length} verbleibend)
                </label>
              )}
            </div>
          ) : (
            <label className="flex flex-col items-center justify-center border-2 border-dashed border-slate-700 hover:border-slate-500 rounded-xl p-8 cursor-pointer transition-all">
              <input
                type="file"
                accept=".pdf"
                multiple
                className="hidden"
                onChange={(e) => onFilesChange(Array.from(e.target.files || []).slice(0, 10))}
              />
              <FileText className="w-8 h-8 text-slate-500 mx-auto mb-2" />
              <p className="text-slate-300 font-medium text-sm">PDFs hier ablegen oder klicken</p>
              <p className="text-slate-500 text-xs mt-1">Bis zu 10 Dateien · je max. 20 MB</p>
            </label>
          )}
        </div>
      ) : (
        <div>
          <label className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-2">
            Schriftsatztext (direkt)
          </label>
          <p className="text-slate-500 text-xs mb-2">
            Für Labor-Tests oder wenn kein PDF vorliegt — mindestens {textMinChars} Zeichen.
          </p>
          <textarea
            value={schriftsatzText}
            onChange={(e) => {
              onSchriftsatzTextChange(e.target.value);
              onValidationErrorClear();
            }}
            rows={10}
            disabled={uploading}
            placeholder="Klage des Dr. Max Mustermann gegen die Muster GmbH, FN 123456a. Beklagte: Alpentech GmbH. Klagebegehren EUR 850,00."
            className="w-full bg-slate-800/60 border border-slate-700 rounded-xl px-3 py-2.5 text-sm text-slate-200 placeholder:text-slate-600 focus:border-amber-500/50 focus:outline-none resize-y min-h-[160px]"
          />
          <p className="text-slate-500 text-[10px] mt-1 text-right">
            {schriftsatzText.trim().length} / {textMinChars} Zeichen min.
          </p>
        </div>
      )}

      <div
        className={`bg-amber-500/10 border rounded-xl p-4 ${!optIn && validationError ? 'border-red-500/40' : 'border-amber-500/20'}`}
      >
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={optIn}
            onChange={(e) => {
              onOptInChange(e.target.checked);
              if (e.target.checked) onValidationErrorClear();
            }}
            className="mt-0.5 accent-amber-500"
          />
          <span className="text-slate-300 text-sm leading-relaxed">
            Ich bestätige, dass ich berechtigt bin, dieses Dokument hochzuladen, und stimme der KI-Analyse zu. Meine
            Kanzlei kann Texte vorab anonymisieren (z. B. „Mandant A"). ARKORBIT pseudonymisiert vor der
            KI-Verarbeitung ein weiteres Mal.
          </span>
        </label>
      </div>

      {validationError && (
        <div className="flex items-start gap-2 text-red-400 text-sm bg-red-500/10 border border-red-500/25 rounded-xl px-3 py-2.5">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{validationError}</span>
        </div>
      )}

      <button
        type="button"
        onClick={onUpload}
        disabled={uploading}
        className="w-full bg-amber-500 hover:bg-amber-400 disabled:bg-slate-700 disabled:cursor-not-allowed text-black disabled:text-slate-500 font-bold py-3 rounded-xl transition-all flex items-center justify-center gap-2"
      >
        {uploading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" /> Analyse läuft — bitte warten (bis zu 5 Min.)…
          </>
        ) : (
          <>
            <FileText className="w-4 h-4" /> Analyse starten
          </>
        )}
      </button>
    </div>
  </div>
);
